import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { formatCost } from '../utils/formatCost';
import { Button } from './Button/Button';
import { Text } from './Text/Text';
import { Container } from './Container/Container';
import { Icon } from './Icon/Icon';
import { IconName } from './Icon/Icon.type';
import { Clock, History } from 'lucide-react';

interface CreditViewPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CreditInfo {
  user_id: string;
  plan_type: string;
  unlimited_access?: boolean;
  total_lifetime_usage?: number;
  credits: {
    [key: string]: {
      amount: number;
      expires?: string;
      remaining?: number;
      original?: number;
      used?: number;
      percentage_used?: number;
      entries?: any[];
      count?: number;
      total_count?: number;
    };
  };
  total_available: number;
  next_reset?: string | number;  // Can be either date string or Unix timestamp
}

interface CreditTransaction {
  id: string;
  date: string;
  date_display: string;
  transaction_type: string;
  credit_type: string;
  credit_type_display: string;
  amount: number;
  remaining_amount?: number;
  consumed_amount?: number;
  description: string;
  source: string;
  status?: string;
  exhausted?: boolean;
  balance_before?: Record<string, number>;
  balance_after?: Record<string, number>;
  reference_id?: string;
  reference_type?: string;
  metadata?: Record<string, any>;
}

interface CreditLogsResponse {
  user_id: string;
  transactions: CreditTransaction[];
  pagination: {
    total_count: number;
    offset: number;
    limit: number;
    has_more: boolean;
  };
}


// Helper function to get applicable credit types based on user's plan
const getApplicableCredits = (creditInfo: CreditInfo): [string, any][] => {
  if (!creditInfo) return [];
  
  // Start with base credit types that all plans have
  let applicableCreditTypes = ['free_platform_credits'];
  
  // Add plan-specific credit types based on user's plan
  const planType = creditInfo.plan_type?.toLowerCase();
  
  // Only show monthly_credits for users on monthly plan
  if (planType === 'monthly') {
    applicableCreditTypes.push('monthly_credits');
  }
  
  // All plans can have paid platform credits
  applicableCreditTypes.push('paid_platform_credits');
  
  return applicableCreditTypes.map(creditType => {
    const credit = creditInfo.credits[creditType];
    
    // If the credit type exists in the data
    if (credit) {
      const remaining = credit.remaining || credit.amount || 0;
      
      // If credit is exactly zero (exhausted but not negative), show $0/$0 format
      // For negative balances (debt), we want to show the actual negative amount and original
      if (remaining === 0 && (credit.original === 0 || !credit.original)) {
        return [creditType, {
          amount: 0,
          remaining: 0,
          original: 0, // Show 0 as original for truly exhausted credits (so it displays as $0/$0)
          used: credit.used || credit.original || 0,
          percentage_used: credit.percentage_used || 100,
          count: 0, // Show 0 grants for exhausted credits
          entries: [] // No active entries to show
        }];
      }
      return [creditType, credit];
    } else {
      // Show 0 entry for credit types that are not available
      return [creditType, {
        amount: 0,
        remaining: 0,
        original: 0,
        used: 0,
        percentage_used: 0,
        count: 0,
        entries: []
      }];
    }
  });
};

// Skeleton Loading Component for Credit Panel
const CreditPanelSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col gap-bg-v2-xl w-full animate-fade-in">
      {/* Current Plan Section Skeleton */}
      <div className="bg-bg-brand-primary-alt border border-border-secondary rounded-bg-v2-xl p-[21px] flex flex-col gap-bg-v2-xl">
        <div className="flex items-center justify-between">
          <div className="h-5 skeleton-shimmer rounded w-24"></div>
          <div className="h-5 skeleton-shimmer rounded-bg-v2-full w-20"></div>
        </div>
        
        {/* Available Credits Skeleton */}
        <div className="flex flex-col gap-bg-v2-xs items-center">
          <div className="h-10 skeleton-shimmer rounded w-32"></div>
          <div className="h-4 skeleton-shimmer rounded w-28"></div>
        </div>
      </div>

      {/* Available Credits Section Skeleton */}
      <div className="flex flex-col gap-bg-v2-lg">
        <div className="h-5 skeleton-shimmer rounded w-32"></div>
        
        <div className="flex flex-col gap-bg-v2-lg">
          {/* Credit Type Cards Skeleton */}
          {[...Array(3)].map((_, index) => (
            <div 
              key={index}
              className="border border-border-secondary rounded-bg-v2-lg p-[17px] flex items-center justify-between bg-bg-primary"
            >
              <div className="flex items-center gap-bg-v2-lg">
                <div className="bg-bg-brand-primary-alt flex items-center justify-center rounded-bg-v2-lg w-9 h-9 skeleton-shimmer"></div>
                <div className="flex flex-col gap-bg-v2-xs">
                  <div className="h-4 skeleton-shimmer rounded w-24"></div>
                  <div className="h-3 skeleton-shimmer rounded w-16"></div>
                </div>
              </div>
              <div className="h-5 skeleton-shimmer rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Credit History Section Skeleton */}
      <div className="border border-border-secondary rounded-bg-v2-lg overflow-hidden flex flex-col flex-1 min-h-0 bg-bg-primary">
        <div className="border-b border-border-secondary px-bg-v2-xl py-bg-v2-xl flex items-center gap-bg-v2-xs">
          <div className="h-5 skeleton-shimmer rounded w-28"></div>
        </div>
        
        <div className="flex-1">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="px-bg-v2-xl py-bg-v2-lg border-b border-border-secondary">
              <div className="flex items-start justify-between gap-bg-v2-md">
                <div className="flex-1 flex flex-col gap-bg-v2-xs">
                  <div className="h-4 skeleton-shimmer rounded w-32"></div>
                  <div className="h-3 skeleton-shimmer rounded w-40"></div>
                  <div className="h-3 skeleton-shimmer rounded w-36"></div>
                </div>
                <div className="h-4 skeleton-shimmer rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Adjust credit displays to show net spendable amounts by offsetting negative balances
 * from positive ones. This ensures displayed amounts match the overall total.
 * Also adjusts the active count to reflect entries that still have positive balance after offsetting.
 */
const adjustCreditsForDebt = (creditInfo: CreditInfo | null): CreditInfo | null => {
  if (!creditInfo?.credits) return creditInfo;
  
  const creditTypes = ['free_platform_credits', 'paid_platform_credits', 'monthly_credits'];
  
  // Step 1: Calculate total debt (sum of all negative balances)
  let totalDebt = 0;
  creditTypes.forEach(type => {
    const credit = creditInfo.credits[type];
    const remaining = credit?.remaining || 0;
    if (remaining < 0) {
      totalDebt += Math.abs(remaining);
    }
  });
  
  // If no debt, return as-is
  if (totalDebt === 0) return creditInfo;
  
  // Step 2: Offset debt from positive balances in priority order
  const adjustedCredits = { ...creditInfo.credits };
  let remainingDebt = totalDebt;
  
  // Priority: free_platform → paid_platform → monthly
  const offsetOrder: (keyof typeof adjustedCredits)[] = [
    'free_platform_credits', 
    'paid_platform_credits', 
    'monthly_credits'
  ];
  
  for (const type of offsetOrder) {
    if (remainingDebt <= 0) break;
    
    const credit = adjustedCredits[type];
    const remaining = credit?.remaining || 0;
    
    if (remaining > 0) {
      const offset = Math.min(remaining, remainingDebt);
      const newRemaining = remaining - offset;
      
      adjustedCredits[type] = {
        ...credit,
        remaining: newRemaining
      };
      
      // If this credit type is now exhausted (remaining <= 0), reduce active count
      if (newRemaining <= 0 && (credit?.count || 0) > 0) {
        adjustedCredits[type] = {
          ...adjustedCredits[type],
          count: 0  // All entries in this type are now exhausted after offset
        };
      }
      
      remainingDebt -= offset;
    }
  }
  
  // Step 3: Zero out any remaining negative balances
  creditTypes.forEach(type => {
    const credit = adjustedCredits[type];
    if (credit && (credit.remaining || 0) < 0) {
      adjustedCredits[type] = {
        ...credit,
        remaining: 0,
        count: 0  // Negative balance means all entries exhausted
      };
    }
  });
  
  return {
    ...creditInfo,
    credits: adjustedCredits
  };
};

const CreditViewPanel: React.FC<CreditViewPanelProps> = ({ isOpen, onClose }) => {
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creditLogs, setCreditLogs] = useState<CreditLogsResponse | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Set loading to true before starting both API calls
      setLoading(true);
      setLogsLoading(true);
      
      // Fetch both in parallel
      Promise.all([
        fetchCreditInfo(),
        fetchCreditProvisionLogs()
      ]).finally(() => {
        // Only hide skeleton after both are complete
        setLoading(false);
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };


  const fetchCreditInfo = async () => {
    setError(null);
    try {
      // Use authenticated endpoint without test user ID
      const response = await axios.get('/billing/my-credits');
      const data = response.data || {};
      
      // Adapt backend response to expected CreditInfo shape - support both old and new formats
      const adapted: CreditInfo = {
        user_id: data.user_id ?? '',
        plan_type: data.plan_type ?? 'starter',
        unlimited_access: data.unlimited_access ?? false,
        total_lifetime_usage: data.total_lifetime_usage ?? 0,
        credits: {
          // Support new billing service format (direct access), fallback to old nested format
          free_platform_credits: { 
            amount: data.free_platform_credits?.remaining ?? data.credits?.free_platform_credits?.remaining ?? data.free_platform_credits ?? 0,
            remaining: data.free_platform_credits?.remaining ?? data.credits?.free_platform_credits?.remaining ?? data.free_platform_credits ?? 0,
            original: data.free_platform_credits?.original ?? data.credits?.free_platform_credits?.original ?? 0,
            used: data.free_platform_credits?.used ?? data.credits?.free_platform_credits?.used ?? 0,
            percentage_used: data.free_platform_credits?.percentage_used ?? data.credits?.free_platform_credits?.percentage_used ?? 0,
            entries: data.free_platform_credits?.entries ?? data.credits?.free_platform_credits?.entries ?? [],
            count: data.free_platform_credits?.count ?? data.credits?.free_platform_credits?.count ?? 0,
            total_count: data.free_platform_credits?.total_count ?? data.credits?.free_platform_credits?.total_count ?? 0
          },
          monthly_credits: {
            amount: data.monthly_credits?.remaining ?? data.credits?.monthly_credits?.remaining ?? data.monthly_credits ?? 0,
            remaining: data.monthly_credits?.remaining ?? data.credits?.monthly_credits?.remaining ?? data.monthly_credits ?? 0,
            original: data.monthly_credits?.original ?? data.credits?.monthly_credits?.original ?? 0,
            used: data.monthly_credits?.used ?? data.credits?.monthly_credits?.used ?? 0,
            percentage_used: data.monthly_credits?.percentage_used ?? data.credits?.monthly_credits?.percentage_used ?? 0,
            entries: data.monthly_credits?.entries ?? data.credits?.monthly_credits?.entries ?? [],
            count: data.monthly_credits?.count ?? data.credits?.monthly_credits?.count ?? 0,
            total_count: data.monthly_credits?.total_count ?? data.credits?.monthly_credits?.total_count ?? 0
          },
          paid_platform_credits: {
            amount: data.paid_platform_credits?.remaining ?? data.credits?.paid_platform_credits?.remaining ?? data.paid_platform_credits ?? 0,
            remaining: data.paid_platform_credits?.remaining ?? data.credits?.paid_platform_credits?.remaining ?? data.paid_platform_credits ?? 0,
            original: data.paid_platform_credits?.original ?? data.credits?.paid_platform_credits?.original ?? 0,
            used: data.paid_platform_credits?.used ?? data.credits?.paid_platform_credits?.used ?? 0,
            percentage_used: data.paid_platform_credits?.percentage_used ?? data.credits?.paid_platform_credits?.percentage_used ?? 0,
            entries: data.paid_platform_credits?.entries ?? data.credits?.paid_platform_credits?.entries ?? [],
            count: data.paid_platform_credits?.count ?? data.credits?.paid_platform_credits?.count ?? 0,
            total_count: data.paid_platform_credits?.total_count ?? data.credits?.paid_platform_credits?.total_count ?? 0
          },
        },
        total_available: (data.total_available ?? data.total_available_credits ?? data.total_credits ?? 0),
        next_reset: data.next_monthly_reset ?? undefined,
      };
      
      setCreditInfo(adapted);
    } catch (err: any) {
      console.error('Failed to fetch credit info:', err);
      setError(err.response?.data?.detail || 'Failed to load credit information');
    }
  };

  const fetchCreditProvisionLogs = async () => {
    try {
      // Only fetch credit provision entries (exclude usage_deduction)
      const params = new URLSearchParams();
      params.append('exclude_transaction_type', 'usage_deduction');
      params.append('limit', '20'); // Show last 20 credit provision entries
      
      const url = `/billing/my-credit-logs?${params.toString()}`;
      const response = await axios.get(url);
      
      // Handle case where API returns empty array instead of expected object structure
      let logsData;
      if (Array.isArray(response.data)) {
        // API returned an array directly (empty array in our case)
        logsData = {
          user_id: 'anonymous',
          transactions: response.data,
          pagination: {
            total_count: response.data.length,
            offset: 0,
            limit: 20,
            has_more: false
          }
        };
      } else {
        // API returned the expected object structure
        logsData = response.data;
      }
      
      setCreditLogs(logsData);
    } catch (err: any) {
      console.error('Failed to fetch credit logs:', err);
      setError(err.response?.data?.detail || 'Failed to load credit provision logs');
    }
  };

  const getPlanTypeDisplay = (planType: string) => {
    switch (planType?.toLowerCase()) {
      case 'starter': return { name: 'Starter Plan' };
      case 'monthly': return { name: 'Monthly Plan' };
      case 'unlimited': return { name: 'Unlimited Plan' };
      default: return { name: planType || 'Plan' };
    }
  };

  const getCreditIcon = (creditType: string) => {
        switch (creditType) {
      case 'free_platform_credits': 
        return  <Icon name={IconName.Gift} size="sm" className="text-border-brand-icn w-bg-v2-xl h-bg-v2-xl" inheritColor={true} />;
      case 'monthly_credits': 
        return <Icon name={IconName.DollarCircle} size="sm" className="text-border-brand-icn w-bg-v2-xl h-bg-v2-xl" inheritColor={true} />;
      case 'paid_platform_credits': 
        return <Icon name={IconName.Wallet} size="sm" className="text-border-brand-icn w-bg-v2-xl h-bg-v2-xl" inheritColor={true} />;
      default: 
        return <Icon name={IconName.DollarCircle} size="sm" className="text-border-brand-icn w-bg-v2-xl h-bg-v2-xl" inheritColor={true} />;
    }
  };

  const formatNextReset = (dateValue?: string | number) => {
    if (!dateValue) return null;
    try {
      // Handle both Unix timestamps (numbers) and date strings
      const date = typeof dateValue === 'number' 
        ? new Date(dateValue * 1000)  // Convert Unix timestamp to milliseconds
        : new Date(dateValue);
      
      // Check if date is valid
      if (isNaN(date.getTime())) return null;
      
      return date.toLocaleString();
    } catch {
      return null;
    }
  };

  const formatDateToLocal = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Format to match Figma: YYYY-MM-DD H:MM AM/PM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
    } catch {
      return dateString;
    }
  };

  const formatTransactionType = (transactionType: string) => {
    const types: Record<string, string> = {
      'initial_signup': 'Welcome Bonus',
      'monthly_reset': 'Monthly Plan Reset',
      'admin_free_credits': 'Admin Credit Grant',
      'admin_adjustment': 'Admin Adjustment',
      'payment_purchase': 'Credit Purchase',
      'refund': 'Refund',
      'bonus': 'Bonus Credit'
    };
    return types[transactionType] || transactionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className={`absolute right-0 top-0 h-full bg-bg-primary w-full max-w-[400px]  flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
            <div className="flex flex-col flex-1">
              <Text variant="text-lg" weight="semibold" color="primary" as="h2">
                My Credits
              </Text>
            </div>
            <Button
              onClick={handleClose}
              variant="ghost"
              size="small"
              className="flex-shrink-0 !rounded-full !p-bg-v2-lg text-fg-secondary"
              label={<Icon name={IconName.Cross} size="md" inheritColor />}
              type="button"
            />
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-bg-v2-xl py-bg-v2-xl flex flex-col gap-bg-v2-xl w-full">

          {loading && <CreditPanelSkeleton />}

          {error && !loading && (
            <Container variant="transparent" padding="lg" className="border border-border-error rounded-bg-v2-md bg-bg-error-secondary">
              <Text variant="text-sm" weight="medium" color="error">
                Error loading credits: {error}
              </Text>
            </Container>
          )}

          {/* Main Content */}
          {creditInfo && !loading && (
            <>
              {/* Current Plan Section */}
              <Container padding="none" className="!bg-bg-brand-primary-alt border border-border-secondary rounded-bg-v2-xl">
                <div className="p-[21px] flex flex-col gap-bg-v2-xl"> {/* p-[21px] - No exact spacing token match, keeping hardcoded */}
                  <div className="flex items-center justify-between">
                    <Text variant="text-md" weight="semibold" color="primary">
                      Current Plan
                    </Text>
                    <span className="bg-bg-primary border border-border-brand-alt px-bg-v2-md py-bg-v2-xxs rounded-bg-v2-full">
                      <Text variant="text-xs" weight="medium" color="brand-secondary">
                        {getPlanTypeDisplay(creditInfo.plan_type).name}
                      </Text>
                    </span>
                  </div>
                  {/* Available Credits in Center */}
                  <div className="flex flex-col gap-bg-v2-xs items-center"> {/* gap-bg-v2-xs (4px) - closest match to gap-1 (4px) */}
                    {creditInfo?.unlimited_access ? (
                      <>
                        <Text 
                          variant="display-xs" 
                          weight="semibold" 
                          color="primary"
                          align="center"
                        >
                          ∞ Unlimited
                        </Text>
                        <Text variant="text-xs" weight="medium" color="disabled" align="center">
                          Remaining Credits
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text 
                          variant="display-xs" 
                          weight="semibold" 
                          color={(creditInfo?.total_available || 0) < 0 ? "error" : "primary"}
                          align="center"
                        >
                          {formatCost(creditInfo?.total_available)}
                        </Text>
                        <Text variant="text-xs" weight="medium" color="disabled" align="center">
                          {(creditInfo?.total_available || 0) < 0 
                            ? 'Credit Debt' 
                            : 'Remaining Credits'
                          }
                        </Text>
                      </>
                    )}
                  </div>
                </div>
              </Container>

              {/* Available Credits Section */}
              <div className="flex flex-col gap-bg-v2-lg">
                <Text variant="text-md" weight="semibold" color="primary">
                  Available Credits
                </Text>
                
                <div className="flex flex-col gap-bg-v2-lg">
                  {getApplicableCredits(adjustCreditsForDebt(creditInfo) || creditInfo).map(([creditType, credit]) => (
                    <div 
                      key={creditType} 
                      className="border border-border-secondary rounded-bg-v2-lg p-[17px] flex items-center justify-between" /* p-[17px] - No exact spacing token match, keeping hardcoded */
                    >
                      <div className="flex items-center gap-bg-v2-lg">
                        <div className="bg-bg-brand-primary-alt flex items-center justify-center rounded-bg-v2-lg w-9 h-9"> {/* w-9 h-9 (36px) - No exact spacing token match, keeping hardcoded */}
                          {getCreditIcon(creditType)}
                        </div>
                        <div className="flex flex-col">
                          <Text variant="text-sm" weight="medium" color="primary">
                            {creditType === 'free_platform_credits' ? 'Free Credits' : 
                             creditType === 'paid_platform_credits' ? 'Paid Credits' :
                             creditType === 'monthly_credits' ? 'Monthly Credits' : creditType}
                          </Text>
                          {(credit.total_count || 0) > 0 && (
                            <Text variant="text-xs" weight="medium" color="disabled">
                              {credit.total_count || 0} grant{(credit.total_count || 0) !== 1 ? 's' : ''}
                              {credit.count !== undefined && credit.total_count > credit.count && (
                                <Text variant="text-xs" color="error" className="ml-bg-v2-xs inline">
                                  {' '}({credit.total_count - credit.count} exhausted)
                                </Text>
                              )}
                            </Text>
                          )}
                        </div>
                      </div>
                      <div>
                        <Text variant="text-md" weight="semibold" color="primary">
                          {credit.remaining !== undefined && credit.original !== undefined
                            ? `${formatCost(Math.max(0, credit.remaining))}/${formatCost(credit.original)}`
                            : formatCost(credit.amount)
                          }
                        </Text>
                      </div>
                      {/* Individual Credit Entries - Show both active and exhausted */}
                    {credit.entries && credit.entries.length > 0 && (
                      <div className="p-bg-v2-xl space-y-[var(--spacing-lg)]"> {/* space-y not available with bg-v2-lg in tailwind.config.js, using CSS var directly */}
                        {/* Active Entries */}
                        {credit.entries
                          .filter((entry: any) => !entry.exhausted && entry.remaining_amount > 0)
                          .map((entry: any, index: number) => (
                          <div key={entry.id} className="flex items-center justify-between p-bg-v2-lg bg-bg-primary border border-[var(--color-utility-gray-100)] rounded-bg-v2-lg"> {/* border-[var(--color-utility-gray-100)] - utility-gray-100 not in tailwind.config.js, using CSS var directly */}
                            <div className="flex items-center gap-bg-v2-lg">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-[var(--color-utility-blue-100)] rounded-bg-v2-full flex items-center justify-center"> {/* w-8 h-8 (32px), utility-blue-100 not in tailwind.config.js, using CSS var directly */}
                                  <span className="text-sm font-medium text-[var(--color-utility-blue-600)]">{index + 1}</span> {/* utility-blue-600 not in tailwind.config.js, using CSS var directly */}
                                </div>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-text-primary">
                                  {entry.source === 'admin_grant' ? 'Admin Grant' :
                                   entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                   entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                   entry.source || 'Credit Grant'}
                                </div>
                                <div className="text-xs text-text-secondary">
                                  {entry.description || 'No description'}
                                </div>
                                {entry.created_at && (
                                  <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                    Granted: {new Date(entry.created_at * 1000).toLocaleDateString()}
                                  </div>
                                )}
                                {entry.expires && (
                                  <div className="text-xs text-[var(--color-utility-orange-600)] flex items-center mt-bg-v2-xs"> {/* utility-orange-600 not in tailwind.config.js, using CSS var directly */}
                                    <Clock className="w-3 h-3 mr-bg-v2-xs" /> {/* w-3 h-3 (12px) - No exact spacing token match, keeping hardcoded */}
                                    Expires: {entry.expires}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-text-primary">
                                {formatCost(Math.max(0, entry.remaining_amount))}
                              </div>
                              <div className="text-xs text-text-secondary">
                                of {formatCost(entry.original_amount)}
                              </div>
                              {entry.remaining_amount > 0 && entry.remaining_amount < entry.original_amount && (
                                <div className="text-xs text-[var(--color-utility-blue-600)] mt-bg-v2-xs"> {/* utility-blue-600 not in tailwind.config.js, using CSS var directly */}
                                  {Math.round((entry.remaining_amount / entry.original_amount) * 100)}% remaining
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        
                        {/* Exhausted Entries */}
                        {credit.entries
                          .filter((entry: any) => entry.exhausted || entry.remaining_amount <= 0)
                          .map((entry: any, index: number) => (
                          <div key={entry.id} className="flex items-center justify-between p-bg-v2-lg bg-bg-secondary-alt border border-border-secondary rounded-bg-v2-lg opacity-75">
                            <div className="flex items-center gap-bg-v2-lg">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-bg-tertiary rounded-bg-v2-full flex items-center justify-center"> {/* w-8 h-8 (32px) - No exact spacing token match, keeping hardcoded */}
                                  <span className="text-sm font-medium text-text-secondary">✓</span>
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-bg-v2-md">
                                  <Text variant="text-sm" weight="medium" color="secondary">
                                    {entry.source === 'admin_grant' ? 'Admin Grant' :
                                     entry.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                     entry.source === 'signup_bonus' ? 'Welcome Bonus' :
                                     entry.source || 'Credit Grant'}
                                  </Text>
                                  <Container 
                                    variant="transparent" 
                                    padding="xs" 
                                    className="bg-[var(--color-utility-error-100)] border border-[var(--color-utility-error-200)] rounded-bg-v2-full" /* utility-error-100, utility-error-200 not in tailwind.config.js, using CSS var directly */
                                  >
                                    <Text variant="text-xs" weight="medium" color="error">
                                      Exhausted
                                    </Text>
                                  </Container>
                                </div>
                                <div className="text-xs text-text-secondary">
                                  {entry.description || 'No description'}
                                </div>
                                {entry.created_at && (
                                  <div className="text-xs text-text-tertiary mt-bg-v2-xs">
                                    Granted: {new Date(entry.created_at * 1000).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-text-secondary line-through">
                                {formatCost(entry.original_amount)}
                              </div>
                              <div className="text-xs text-text-error-primary">
                                Fully Used
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    </div>
                  ))}

                </div>
              </div>

              {/* Credit History Section */}
              <div className="border border-border-secondary rounded-bg-v2-lg overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="border-b border-border-secondary px-bg-v2-xl py-bg-v2-xl flex items-center justify-between flex-shrink-0">
                  <div className="flex items-center gap-bg-v2-xs">
                  <History className="w-4 h-4" strokeWidth={2} style={{ color: 'var(--color-text-tertiary)' }} /> {/* w-4 h-4 (16px) - No exact spacing token match, keeping hardcoded */}
                    <Text variant="text-md" weight="semibold" color="primary">
                      Credit History
                    </Text>
                  </div>
                </div>

                {/* Transaction List */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {creditLogs && creditLogs.transactions && creditLogs.transactions.length > 0 ? (
                    <div className="divide-y divide-border-secondary">
                      {creditLogs.transactions.map((transaction) => {
                        const isExhausted = transaction.status === 'exhausted' || 
                                          transaction.exhausted === true || 
                                          (transaction.remaining_amount !== undefined && transaction.remaining_amount <= 0);
                        const isPositive = transaction.amount > 0;
                        
                        return (
                          <div 
                            key={transaction.id} 
                            className="px-bg-v2-xl py-bg-v2-lg relative"
                          >
                            <div className="flex items-start justify-between gap-bg-v2-md">
                              <div className="flex-1 min-w-0 flex flex-col gap-bg-v2-xxs">
                                <div className="flex items-center gap-bg-v2-xs">
                                  <Text variant="text-sm" weight="medium" color="primary">
                                    {transaction.credit_type_display || transaction.credit_type} Credits
                                  </Text>
                                  {isExhausted && (
                                    <Container 
                                      variant="error-secondary" 
                                      padding="xs" 
                                      className="bg-[var(--color-utility-error-100)] border border-[var(--color-utility-error-200)] rounded-bg-v2-full"
                                    >
                                      <Text variant="text-xs" weight="medium" color="error" className='px-1'>
                                        Exhausted
                                      </Text>
                                    </Container>
                                  )}
                                </div>
                                <Text variant="text-xs" weight="medium" color="tertiary">
                                  Source: {transaction.source === 'admin_grant' ? 'Admin Grant' :
                                          transaction.source === 'monthly_allocation' ? 'Monthly Allocation' :
                                          transaction.source === 'signup_bonus' ? 'Welcome Bonus' :
                                          transaction.source || 'System'}
                                  {transaction.description && ` • ${transaction.description}`}
                                </Text>
                                <Text variant="text-xs" weight="regular" color={isPositive ? "quaternary" : "tertiary"}>
                                  {formatDateToLocal(transaction.date)}
                                </Text>
                              </div>
                              <div className="text-right">
                                <Text 
                                  variant="text-sm" 
                                  weight="semibold"
                                  color={isExhausted ? "tertiary" : (isPositive ? "success" : "primary")}
                                  className={isExhausted ? "line-through" : ""}
                                >
                                  {isPositive ? '+' : ''}{formatCost(Math.abs(transaction.amount))}
                                </Text>
                                {isExhausted && (
                                  <Text variant="text-xs" color="error" className="mt-bg-v2-xs">
                                    Fully Used
                                  </Text>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {creditLogs.pagination.has_more && (
                        <Container variant="transparent" padding="sm" className="text-center">
                          <Text variant="text-xs" color="tertiary">
                            Showing {creditLogs.transactions.length} of {creditLogs.pagination.total_count} credit additions
                          </Text>
                        </Container>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12"> {/* py-12 (48px) - No exact spacing token match, keeping hardcoded */}
                      <Text variant="text-sm" weight="medium" color="primary" className="mb-bg-v2-xs text-center">
                        No Credit History
                      </Text>
                      <Text variant="text-xs" color="tertiary" className="text-center">
                        No credit additions found for your account.
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default CreditViewPanel;
